/**
 * Luna Mart free gift.
 *
 * Keeps one free Deadpool Miniatures set in the cart while the shopper's other items
 * add up to the threshold (₹2000), and takes it out when they drop below it.
 * Shopify's automatic discount "Free Deadpool Miniatures on ₹2000+" is what makes it
 * free. A gift line is never left in the cart at a price: if the discount is not
 * making it free (discount switched off, or a discount code that doesn't combine
 * with it), the script removes it again and does not re-add it while that stays true.
 *
 * The gift line carries the hidden line property `_luna_gift`, so a set the shopper
 * buys on purpose is never touched. If the shopper removes the gift themselves, it
 * is not added back until their cart drops below the threshold and qualifies again.
 *
 * Cart changes that arrive while a check is running are queued, not dropped, so
 * quick taps on + / − always end with the cart in the right state.
 *
 * Settings come from data attributes on the script tag (snippets/luna-gift.liquid).
 */
(() => {
  const script = document.currentScript;
  if (!script) return;

  const GIFT_VARIANT = Number(script.dataset.variant);
  const GIFT_PRODUCT = Number(script.dataset.product);
  const THRESHOLD = Number(script.dataset.threshold);
  if (!GIFT_VARIANT || !THRESHOLD) return;

  const PROPERTY = '_luna_gift';
  // Holds the applied discount codes at the moment the gift turned out to be charged.
  const OFF_KEY = 'luna-gift-off';
  const DECLINED_KEY = 'luna-gift-declined';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const store = {
    get(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        if (value == null) window.sessionStorage.removeItem(key);
        else window.sessionStorage.setItem(key, value);
      } catch (error) {
        /* storage blocked: the gift simply follows the cart */
      }
    },
  };

  let busy = false;
  let queued = null;
  let hadGift = null;

  const getCart = () =>
    fetch(root + 'cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((response) => {
      if (!response.ok) throw new Error('cart ' + response.status);
      return response.json();
    });

  const isGift = (item) => item.variant_id === GIFT_VARIANT && item.properties && item.properties[PROPERTY];

  // The applied discount codes, as one string ("-" when there are none).
  const codesOf = (cart) =>
    (cart.discount_codes || [])
      .filter((code) => code.applicable)
      .map((code) => String(code.code).toUpperCase())
      .sort()
      .join(',') || '-';

  // Shopify.actions arrives with a separate script; give it a few seconds on first use.
  const getActions = async () => {
    for (let attempt = 0; attempt < 20; attempt += 1) {
      const actions = window.Shopify && window.Shopify.actions;
      if (actions && typeof actions.updateCart === 'function') return actions;
      await new Promise((resolve) => window.setTimeout(resolve, 250));
    }
    return null;
  };

  // Changes go through Shopify.actions so the theme's cart drawer and count refresh themselves.
  const updateLines = async (lines) => {
    const actions = await getActions();
    if (!actions) throw new Error('Shopify.actions is not available');
    return actions.updateCart({ lines }, { event: { detail: { source: 'luna-gift' } } });
  };

  const reconcile = async (reason) => {
    if (busy) {
      // Run again when the current pass is done. A removal wins, so a gift the
      // shopper deleted mid-check still counts as declined.
      queued = queued === 'remove' || reason === 'remove' ? 'remove' : reason || 'queued';
      return;
    }
    busy = true;

    try {
      const cart = await getCart();
      const items = cart.items || [];
      const giftLines = items.filter(isGift);
      const eligible = items
        .filter((item) => !isGift(item) && item.product_id !== GIFT_PRODUCT)
        .reduce((sum, item) => sum + item.original_line_price, 0);
      const qualifies = eligible >= THRESHOLD;
      const codes = codesOf(cart);
      const charged = giftLines.some((line) => line.final_line_price > 0);
      let hasGift = giftLines.length > 0;

      if (!qualifies) store.set(DECLINED_KEY, null);
      if (reason === 'remove' && hadGift && !hasGift && qualifies) store.set(DECLINED_KEY, '1');

      if (hasGift && (!qualifies || charged)) {
        // Below the threshold, or the discount isn't making it free: never leave a paid "gift".
        if (qualifies && charged) store.set(OFF_KEY, codes);
        await updateLines(giftLines.map((line) => ({ id: line.key, quantity: 0 })));
        hasGift = false;
      } else if (qualifies && !hasGift && store.get(OFF_KEY) !== codes && store.get(DECLINED_KEY) !== '1') {
        await updateLines([
          { merchandiseId: String(GIFT_VARIANT), quantity: 1, attributes: [{ key: PROPERTY, value: 'Free gift' }] },
        ]);
        const after = await getCart();
        const line = (after.items || []).find(isGift);
        hasGift = Boolean(line);
        if (line && line.final_line_price > 0) {
          // The gift discount is not applying, so the set would be charged: take it out.
          store.set(OFF_KEY, codesOf(after));
          await updateLines([{ id: line.key, quantity: 0 }]);
          hasGift = false;
        }
      } else if (hasGift && (giftLines.length > 1 || giftLines[0].quantity > 1)) {
        await updateLines(giftLines.map((line, index) => ({ id: line.key, quantity: index === 0 ? 1 : 0 })));
      }

      hadGift = hasGift;
    } catch (error) {
      console.warn('[luna-gift]', error);
    } finally {
      busy = false;
      if (queued) {
        const next = queued;
        queued = null;
        window.setTimeout(() => reconcile(next), 0);
      }
    }
  };

  const onCartEvent = (delay) => (event) => {
    const action = event.action;
    const run = () => reconcile(action);
    if (event.promise && typeof event.promise.then === 'function') {
      // Re-check after failures too: part of the change may still have gone through.
      event.promise.then(run, run);
    } else {
      window.setTimeout(run, delay);
    }
  };

  document.addEventListener('shopify:cart:lines-update', onCartEvent(500));
  // Applying or removing a discount code can stop the gift discount from applying.
  document.addEventListener('shopify:cart:discount-update', onCartEvent(1500));

  // "Add my free gift" in the cart, for shoppers who removed it earlier.
  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-luna-gift-add]') : null;
    if (!button) return;
    button.disabled = true;
    store.set(DECLINED_KEY, null);
    store.set(OFF_KEY, null);
    reconcile('add');
  });

  if (Number(script.dataset.count) > 0) reconcile('load');
})();
